import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import api from '../../api';
import { filamentLabel } from '../../helpers/filaments';
import { openRestrictionForPrinter, productivityChipLabel } from '../../helpers/printerProductivity';

export default function AssignFilamentToPrintersDialog({
    open,
    onClose,
    filament,
    onAssigned,
}) {
    const [printers, setPrinters] = useState([]);
    const [restrictions, setRestrictions] = useState([]);
    const [selected, setSelected] = useState([]);
    const [locations, setLocations] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        setSelected([]);
        setLocations({});
        api.get('printers', { params: { page: 1, limit: 200 } })
            .then((response) => {
                setPrinters(response.data.printers || []);
            })
            .catch(() => setPrinters([]));
        api.get('printer-productivity-restrictions')
            .then((response) => setRestrictions(response.data?.restrictions || []))
            .catch(() => setRestrictions([]));
        return undefined;
    }, [open]);

    const togglePrinter = (printer) => {
        if (openRestrictionForPrinter(restrictions, printer.id)) {
            return;
        }
        setSelected((prev) => {
            const exists = prev.some((item) => item.id === printer.id);
            if (exists) {
                return prev.filter((item) => item.id !== printer.id);
            }
            return [...prev, printer];
        });
    };

    const handleSave = async () => {
        if (selected.length === 0) {
            await Swal.fire({
                icon: 'warning',
                title: 'Selecione ao menos uma impressora',
            });
            return;
        }
        for (const printer of selected) {
            if (!String(locations[printer.id] || '').trim()) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'Informe o local',
                    text: `Defina o local para ${printer.name}.`,
                });
                return;
            }
        }
        setSaving(true);
        try {
            await api.post(`filaments/${filament.id}/assign-printers`, {
                assignments: selected.map((printer) => ({
                    printer_uuid: printer.id,
                    location: String(locations[printer.id]).trim(),
                })),
            });
            onAssigned?.();
            onClose?.();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Não foi possível vincular',
                text: error?.response?.data?.error || 'Tente novamente.',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                Vincular {filament ? filamentLabel(filament) : 'filamento'}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Escolha uma ou mais impressoras e informe o local em cada uma
                    (Slot 1, Spool externo, CFS 3 - Slot 2…).
                </Typography>
                <Stack spacing={1}>
                    {printers.map((printer) => {
                        const checked = selected.some((item) => item.id === printer.id);
                        const restriction = openRestrictionForPrinter(restrictions, printer.id);
                        return (
                            <Box
                                key={printer.id}
                                sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 1.25,
                                }}
                            >
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={checked}
                                            disabled={Boolean(restriction)}
                                            onChange={() => togglePrinter(printer)}
                                        />
                                    )}
                                    label={restriction
                                        ? `${printer.name} · ${productivityChipLabel(restriction)}`
                                        : printer.name}
                                />
                                {checked && (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Local"
                                        placeholder="Slot 1"
                                        value={locations[printer.id] || ''}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setLocations((prev) => ({
                                                ...prev,
                                                [printer.id]: value,
                                            }));
                                        }}
                                        sx={{ mt: 0.5 }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Stack>
                {printers.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Nenhuma impressora encontrada.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancelar</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    Vincular
                </Button>
            </DialogActions>
        </Dialog>
    );
}
